import { z } from 'zod';
import { VALIDATION } from '../constants';
// ============================================
// Auth Schemas
// ============================================
export const loginSchema = z.object({
    email: z.string().email('Invalid email address'),
    password: z.string().min(1, 'Password is required'),
});
// Identifier-based login: accepts email, NIP, or phone number. The API
// normalizes and classifies the value server-side. Used by legacy users
// migrated from the v2 system (see .migration-reports/HANDOFF.md Phase 2).
export const loginIdentifierSchema = z.object({
    identifier: z.string().min(1, 'Identifier is required'),
    password: z.string().min(1, 'Password is required'),
});
export const registerSchema = z.object({
    name: z
        .string()
        .min(VALIDATION.MIN_NAME_LENGTH, `Name must be at least ${VALIDATION.MIN_NAME_LENGTH} characters`)
        .max(VALIDATION.MAX_NAME_LENGTH, `Name must be at most ${VALIDATION.MAX_NAME_LENGTH} characters`),
    email: z.string().email('Invalid email address'),
    password: z
        .string()
        .min(VALIDATION.MIN_PASSWORD_LENGTH, `Password must be at least ${VALIDATION.MIN_PASSWORD_LENGTH} characters`)
        .max(VALIDATION.MAX_PASSWORD_LENGTH, `Password must be at most ${VALIDATION.MAX_PASSWORD_LENGTH} characters`),
    phone: z
        .string()
        .min(10, 'Phone number must be at least 10 characters')
        .max(20, 'Phone number too long'),
    gender: z.enum(['MALE', 'FEMALE']),
});
// ============================================
// Program Schemas
// ============================================
export const createProgramSchema = z.object({
    code: z.string().min(1, 'Code is required').max(20, 'Code too long'),
    name: z.string().min(1, 'Name is required').max(200, 'Name too long'),
    description: z.string().max(VALIDATION.MAX_DESCRIPTION_LENGTH).optional(),
    coverImage: z.string().optional().nullable(),
    status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']).default('DRAFT'),
    cohort: z.number().int().min(1).default(1),
    generateParticipantNip: z.boolean().default(false),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
});
export const updateProgramSchema = createProgramSchema.partial();
// ============================================
// Registration Schemas
// ============================================
// Mirrors apps/api/src/modules/registrations/dto/registration.dto.ts
// — backend marks email + name as required (used to find-or-create the
// User row). If you change the DTO, change this in lockstep.
export const createRegistrationSchema = z.object({
    programId: z.string().uuid('Invalid program ID'),
    email: z.string().email('email must be an email'),
    name: z.string().min(1, 'name must be a string'),
    phone: z.string().optional(),
    gender: z.enum(['MALE', 'FEMALE']).optional(),
    notes: z.string().optional(),
    domisili: z.string().min(1, 'Domisili is required'),
    reason: z.string().min(1, 'Reason is required'),
    consentGiven: z.boolean(),
});
export const updateRegistrationStatusSchema = z.object({
    status: z.enum(['APPROVED', 'REJECTED', 'CANCELLED']),
    notes: z.string().optional(),
});
// ============================================
// Material Schemas
// ============================================
export const createMaterialSchema = z.object({
    flowId: z.string().uuid('Invalid flow ID'),
    title: z.string().min(1, 'Title is required').max(200),
    description: z.string().max(VALIDATION.MAX_DESCRIPTION_LENGTH).optional(),
    type: z.enum(['VIDEO', 'DOCUMENT', 'QUIZ', 'REFLECTION', 'DISCUSSION']),
    content: z.string().optional(),
    order: z.number().int().min(0),
    duration: z.number().int().min(0).optional(),
    isRequired: z.boolean().default(true),
});
// ============================================
// Evaluation Schemas
// ============================================
export const createEvaluationSchema = z.object({
    materialId: z.string().uuid('Invalid material ID'),
    title: z.string().min(1, 'Title is required').max(200),
    description: z.string().optional(),
    type: z.enum(['QUIZ', 'EXAM', 'SURVEY']),
    passingScore: z.number().int().min(0).max(100).default(70),
    timeLimit: z.number().int().min(0).optional(),
    maxAttempts: z.number().int().min(1).default(1),
});
export const createQuestionSchema = z.object({
    evaluationId: z.string().uuid('Invalid evaluation ID'),
    question: z.string().min(1, 'Question is required'),
    type: z.enum(['MULTIPLE_CHOICE', 'TRUE_FALSE', 'SHORT_ANSWER', 'ESSAY']),
    explanation: z.string().optional(),
    order: z.number().int().min(0),
    points: z.number().int().min(1).default(1),
});
export const submitAnswerSchema = z.object({
    questionId: z.string().uuid('Invalid question ID'),
    optionId: z.string().uuid('Invalid option ID').optional(),
    textAnswer: z.string().max(VALIDATION.MAX_TEXT_ANSWER_LENGTH).optional(),
});
export const submitAttemptSchema = z.object({
    answers: z.array(submitAnswerSchema),
});
// ============================================
// Pagination Schema
// ============================================
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(20),
});
